import { useState, FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useScrollReveal } from '@/hooks/useScrollReveal';

export default function QuoteForm() {
  const { t } = useTranslation('reparation');
  const { ref, isVisible } = useScrollReveal<HTMLDivElement>(0.15);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: '',
    urgent: false,
  });

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);
    fetch(form.action, {
      method: form.method,
      body: data,
      headers: { Accept: 'application/json' },
    }).finally(() => {
      if (window.REACT_APP_NAVIGATE) {
        window.REACT_APP_NAVIGATE('/merci');
      }
    });
  };

  const handleChange = (field: string, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <section id="quote-form" className="bg-white py-16 md:py-20">
      <div
        ref={ref}
        className={`max-w-xl mx-auto px-4 md:px-6 transition-all duration-1000 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
        }`}
      >
        <div className="bg-soft-gray rounded-xl p-6 md:p-10">
          <h3 className="font-manrope font-bold text-slate-primary text-xl md:text-2xl text-center mb-2">
            {t('form_h2')}
          </h3>
          <p className="text-gray-500 text-sm text-center mb-8">{t('form_subtitle')}</p>

          <form
            action="https://readdy.ai/api/form/d84bjtlrvgn6qj6abvjg"
            method="POST"
            data-readdy-form
            onSubmit={handleSubmit}
            className="space-y-4"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="rep-name" className="block text-sm font-medium text-slate-primary mb-1.5">
                  {t('form_name_label')}
                </label>
                <input
                  id="rep-name"
                  name="name"
                  type="text"
                  autoComplete="name"
                  required
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  className="w-full px-4 py-3 rounded-md border border-soft-dark bg-white text-slate-primary text-sm focus:outline-none focus:border-copper transition-colors"
                  placeholder={t('form_name_placeholder')}
                />
              </div>
              <div>
                <label htmlFor="rep-phone" className="block text-sm font-medium text-slate-primary mb-1.5">
                  {t('form_phone_label')}
                </label>
                <input
                  id="rep-phone"
                  name="phone"
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  required
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  className="w-full px-4 py-3 rounded-md border border-soft-dark bg-white text-slate-primary text-sm focus:outline-none focus:border-copper transition-colors"
                  placeholder={t('form_phone_placeholder')}
                />
              </div>
            </div>

            <div>
              <label htmlFor="rep-email" className="block text-sm font-medium text-slate-primary mb-1.5">
                {t('form_email_label')}
              </label>
              <input
                id="rep-email"
                name="email"
                type="email"
                inputMode="email"
                autoComplete="email"
                required
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                className="w-full px-4 py-3 rounded-md border border-soft-dark bg-white text-slate-primary text-sm focus:outline-none focus:border-copper transition-colors"
                placeholder={t('form_email_placeholder')}
              />
            </div>

            <div>
              <label htmlFor="rep-service" className="block text-sm font-medium text-slate-primary mb-1.5">
                {t('form_service_label')}
              </label>
              <select
                id="rep-service"
                name="service"
                value={formData.service}
                onChange={(e) => handleChange('service', e.target.value)}
                className="w-full px-4 py-3 rounded-md border border-soft-dark bg-white text-slate-primary text-sm focus:outline-none focus:border-copper transition-colors"
              >
                <option value="">{t('form_service_select')}</option>
                <option value="infiltration">{t('probleme_1_title')}</option>
                <option value="bardeaux">{t('probleme_2_title')}</option>
                <option value="solins">{t('probleme_3_title')}</option>
                <option value="event">{t('probleme_4_title')}</option>
                <option value="glace">{t('probleme_5_title')}</option>
                <option value="trou">{t('probleme_6_title')}</option>
                <option value="autre">Autre</option>
              </select>
            </div>

            <div className="flex items-start gap-3">
              <input
                id="rep-urgent"
                name="urgent"
                type="checkbox"
                checked={formData.urgent}
                onChange={(e) => handleChange('urgent', e.target.checked)}
                className="w-5 h-5 mt-0.5 rounded border-soft-dark text-urgent focus:ring-urgent cursor-pointer"
              />
              <label htmlFor="rep-urgent" className="text-sm text-slate-primary/80 cursor-pointer">
                {t('form_urgent_label')}
              </label>
            </div>

            <div>
              <label htmlFor="rep-message" className="block text-sm font-medium text-slate-primary mb-1.5">
                {t('form_message_label')}
              </label>
              <textarea
                id="rep-message"
                name="message"
                rows={4}
                maxLength={500}
                value={formData.message}
                onChange={(e) => handleChange('message', e.target.value)}
                className="w-full px-4 py-3 rounded-md border border-soft-dark bg-white text-slate-primary text-sm focus:outline-none focus:border-copper transition-colors resize-none"
                placeholder={t('form_message_placeholder')}
              />
              <p className="text-gray-400 text-xs mt-1 text-right">
                {formData.message.length}{t('form_char_count')}
              </p>
            </div>

            <input
              type="text"
              name="_gotcha"
              className="hidden"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
            />

            <button
              type="submit"
              className="w-full bg-copper hover:bg-copper-dark text-white font-bold py-4 rounded-md text-base transition-all duration-300 flex items-center justify-center gap-2 whitespace-nowrap"
            >
              {t('cta_quote')}
              <i className="ri-arrow-right-line" />
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}